import { Chart, Plugin, TooltipModel } from 'chart.js';
import utils from './utils';
import {
  Area,
  DoughnutChartLabel,
  DoughnutLabelConfig,
  Font,
  LabelProvider,
  ResizeStrategy,
} from '@/features/reports/components/chartjs/plugins/doughnutLabels/types';

declare module 'chart.js' {
  interface PluginOptionsByType<TType> {
    doughnutlabel?: DoughnutLabelConfig;
  }
}

export const DoughnutLabelPlugin: Plugin<'doughnut', DoughnutLabelConfig> = {
  id: 'doughnutlabel',
  afterDraw: (chart) => drawCurrentLabels(chart), // Draw at the end of the render cycle
  afterInit,
};

function afterInit(chart: Chart) {
  if ((chart.config as any)?.type !== 'doughnut') return;
  const options = chart.options;
  if (!options.plugins) options.plugins = {};
  if (!options.plugins.tooltip) options.plugins.tooltip = {};

  options.plugins.tooltip.external = (context: { chart: Chart; tooltip: TooltipModel<'doughnut'> }) => {
    const tooltip = context.tooltip;
    const pluginData = context.chart.options.plugins?.doughnutlabel;
    if (!pluginData) return;

    if (tooltip.opacity === 0) {
      clearLabels(context.chart);
      return;
    }

    const tipData = tooltip.dataPoints[0];
    const key = tipData.label;
    const value = Number(tipData.formattedValue);
    const index = tipData.dataIndex;
    const labelProvider = pluginData.labelProvider;

    const labels = labelProvider ? labelProvider(context.chart, index, key, value) : [];

    pluginData.lastLabels = labels.map((label) => ({
      text: label.text,
      font: parseFont(label.font || {}),
      color: label.color || (defaults.color as string),
    }));
  };

  chart.canvas!.addEventListener('mouseleave', () => {
    clearLabels(chart);
  });

  if (!options.plugins.doughnutlabel?.lastLabels) {
    clearLabels(chart);
  }
}

function drawCurrentLabels(chart: Chart) {
  const pluginData = chart.options.plugins?.doughnutlabel;
  if (!pluginData || !pluginData.lastLabels) return;

  const lastLabels = unProxy<DoughnutChartLabel>(pluginData.lastLabels);
  drawLabels(chart, lastLabels);
}

function clearLabels(chart: Chart) {
  const pluginData = chart.options.plugins?.doughnutlabel;
  if (!pluginData) return;

  const labelProvider = pluginData.labelProvider as LabelProvider;
  const labels = labelProvider(chart, -1, '', undefined);
  pluginData.lastLabels = labels.map((label) => ({
    text: label.text,
    font: parseFont(label.font || {}),
    color: label.color || (defaults.color as string),
  }));
}

function drawLabels(chart: Chart, innerLabels: DoughnutChartLabel[]) {
  const ctx = chart.ctx as CanvasRenderingContext2D;
  const textAreaSize = calculateTextAreaSize(ctx, innerLabels, chart);

  ctx.save(); // Save context state
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
  const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

  const topY = centerY - textAreaSize.height / 2;
  let currentHeight = 0;

  for (const label of innerLabels) {
    ctx.fillStyle = label.color as string;
    ctx.font = label.font.string as string;

    const lineHeight = label.font.lineHeight || 24;
    const lineCenterY = topY + lineHeight / 2 + currentHeight;
    currentHeight += lineHeight;

    ctx.fillText(label.text, centerX, lineCenterY);
  }
  ctx.restore(); // Restore context state
}

// Rest of the functions remain unchanged...
function calculateTextAreaSize(ctx: CanvasRenderingContext2D, innerLabels: DoughnutChartLabel[], chart: Chart): Area {
  const textAreaSize = utils.textSize(ctx, innerLabels);

  if (isTextAreaTooLarge(textAreaSize, chart)) {
    if (resizeStrategy(chart) === ResizeStrategy.SHRINK) {
      return alterLabelsToFit(ctx, innerLabels, textAreaSize, chart);
    } else {
      return truncateLabelsToFit(ctx, innerLabels, chart);
    }
  }

  return textAreaSize;
}

function alterLabelsToFit(
  ctx: CanvasRenderingContext2D,
  innerLabels: DoughnutChartLabel[],
  textAreaSize: Area,
  chart: Chart,
): Area {
  const fitRatio = getAdjustmentRequiredToFitArea(textAreaSize, chart);

  innerLabels.forEach((innerLabel) => {
    innerLabel.font.size = Math.floor(Number(innerLabel.font.size) * fitRatio);
    innerLabel.font.lineHeight = undefined;
    innerLabel.font = parseFont(innerLabel.font) as Font;
  });

  return utils.textSize(ctx, innerLabels);
}

function truncateLabelsToFit(ctx: CanvasRenderingContext2D, innerLabels: DoughnutChartLabel[], chart: Chart): Area {
  for (let i = 0; i < innerLabels.length; i++) {
    const tmpLabels = innerLabels.map((label) => ({ ...label }));
    tmpLabels.forEach((label, index) => (label.text = i === index ? label.text : ' '));
    let textAreaForSingleLabel = utils.textSize(ctx, tmpLabels);
    while (isTextAreaTooLarge(textAreaForSingleLabel, chart)) {
      tmpLabels[i].text = tmpLabels[i].text.substring(0, tmpLabels[i].text.length - 4) + '...';
      tmpLabels[i].font.lineHeight = undefined;
      tmpLabels[i].font = parseFont(tmpLabels[i].font);
      textAreaForSingleLabel = utils.textSize(ctx, tmpLabels);
    }
    innerLabels[i].text = tmpLabels[i].text;
    innerLabels[i].font = tmpLabels[i].font;
  }

  return utils.textSize(ctx, innerLabels);
}

function isTextAreaTooLarge(textAreaSize: Area, chart: Chart): boolean {
  return getAdjustmentRequiredToFitArea(textAreaSize, chart) < 1;
}

function getAdjustmentRequiredToFitArea(textAreaSize: Area, chart: Chart): number {
  const hypotenuse = Math.sqrt(Math.pow(textAreaSize.width, 2) + Math.pow(textAreaSize.height, 2));
  const innerDiameter = (chart as any).innerRadius * 2;
  return innerDiameter / hypotenuse;
}

function resizeStrategy(chart: Chart): ResizeStrategy {
  const pluginData = chart.options.plugins?.doughnutlabel;
  return pluginData?.resizeStrategy ?? ResizeStrategy.SHRINK;
}

function parseFont(fontSpec: Font): Font {
  const defaults = Chart.defaults.font;
  return {
    family: fontSpec.family || defaults.family || '',
    size: fontSpec.size || defaults.size || 10,
    style: fontSpec.style || defaults.style || '',
    weight: fontSpec.weight || defaults.weight || 'normal',
    lineHeight: fontSpec.lineHeight || <number>defaults.lineHeight || 10,
    string: `${fontSpec.style || defaults.style} ${fontSpec.weight || ''} ${fontSpec.size || defaults.size}px ${
      fontSpec.family || defaults.family
    }`,
  };
}

function unProxy<T>(partialArray: any[]): T[] {
  return partialArray.map((partial) => ({ ...partial }));
}

const defaults = Chart.defaults;
